import React, { useEffect, useState } from "react";
import { Alert, Avatar, Box, Button, Chip, CircularProgress, Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import LinkIcon from "@mui/icons-material/Link";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import axios from "axios";
import Api from "../../customlib/Api";
import Global from "../../customlib/Global";
import { getAvatarColor, getInitials } from "./livechatDisplayUtils";
import { PASTEL } from "./livechatTheme";

// Riwayat order (FR-A-07/08) — reuse endpoint Lavender "profile-customer" yang sama dengan
// yang dipakai src/components/CustomerProfile/index.jsx#loadCustomerQna (data.data.lastOrder).
// Bukan endpoint POS terpisah — cuma order terakhir, bukan daftar riwayat lengkap.
export default function CustomerContextPanel({ session, onGenerateLink, onClose }) {
    const [lastOrder, setLastOrder] = useState(null);
    const [isLoadingOrder, setIsLoadingOrder] = useState(false);

    useEffect(() => {
        if (!session?.customerPhone) {
            setLastOrder(null);
            return;
        }
        setIsLoadingOrder(true);
        setLastOrder(null);
        axios
            .get(Api.lavenderGetCustomerQna(session.customerPhone), Api.getRequestHeaderNoAuth())
            .then(({ data }) => setLastOrder(data?.data?.lastOrder || null))
            .catch(() => setLastOrder(null))
            .finally(() => setIsLoadingOrder(false));
    }, [session?.customerPhone]);

    if (!session) return null;

    return (
        <Box sx={{
            width: 280, flexShrink: 0, borderLeft: `1px solid ${PASTEL.border}`, backgroundColor: PASTEL.surface,
            display: "flex", flexDirection: "column",
        }}>
            <Box sx={{ p: 2.5, pt: 1.5, textAlign: "center", borderBottom: `1px solid ${PASTEL.border}`, position: "relative" }}>
                {onClose && (
                    <Tooltip title="Sembunyikan panel">
                        <IconButton
                            size="small" onClick={onClose}
                            sx={{ position: "absolute", top: 4, right: 4, color: "text.secondary" }}
                        >
                            <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                <Avatar sx={{ bgcolor: getAvatarColor(session.customerName), width: 56, height: 56, fontSize: 20, mx: "auto", mb: 1 }}>
                    {getInitials(session.customerName)}
                </Avatar>
                <Typography variant="subtitle1" fontWeight={700}>{session.customerName}</Typography>
                <Typography variant="body2" color="text.secondary">{session.customerPhone}</Typography>
            </Box>

            <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Informasi Kontak
                </Typography>
                <Stack spacing={1.5} sx={{ mt: 1, mb: 2 }}>
                    <Row label="Kanal" value={session.brand} />
                    <Row label="Chat pertama" value={Global.formatDateTimeToStrDateTime24(session.created_at)} />
                    {session.originUrl && <Row label="Halaman asal" value={session.originUrl} isLink />}
                    <Row
                        label="Status"
                        value={
                            <Chip
                                size="small"
                                label={session.status === "active" ? "Aktif" : "Selesai"}
                                sx={session.status === "active"
                                    ? { fontWeight: 600, mt: 0.25, backgroundColor: PASTEL.primary, color: "#fff" }
                                    : { fontWeight: 600, mt: 0.25 }}
                            />
                        }
                    />
                </Stack>

                <Divider sx={{ mb: 2 }} />

                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Order Terakhir
                </Typography>

                {isLoadingOrder && (
                    <Box textAlign="center" sx={{ py: 2 }}>
                        <CircularProgress size={20} />
                    </Box>
                )}

                {!isLoadingOrder && !lastOrder && (
                    <Alert
                        severity="info" variant="outlined" icon={<InfoOutlinedIcon fontSize="small" />}
                        sx={{ borderRadius: 2, fontSize: 12, mt: 1, mb: 2 }}
                    >
                        Belum ada riwayat order untuk nomor ini.
                    </Alert>
                )}

                {!isLoadingOrder && lastOrder && (
                    <Box sx={{ mt: 1, mb: 2 }}>
                        <Box textAlign="center" sx={{ mb: 1.5 }}>
                            <Avatar
                                variant="rounded"
                                src={`${Api.getLavenderUrl()}/${lastOrder?.cartLast?.image}?w=200&fit=crop&auto=format`}
                                sx={{ width: 72, height: 72, mx: "auto", mb: 1 }}
                            />
                            {lastOrder?.cartLast?.product_code && (
                                <Chip size="small" label={lastOrder.cartLast.product_code} variant="outlined" />
                            )}
                        </Box>
                        <Stack spacing={1.25}>
                            <Row
                                label="Nomor CO"
                                value={
                                    <Button
                                        size="small" variant="text" target="_blank" rel="noreferrer"
                                        href={`https://sakura.prestisa.id/orders/view/${lastOrder?.order_number}`}
                                        endIcon={<OpenInNewIcon sx={{ width: 12, height: 12 }} />}
                                        sx={{ fontSize: 12, textTransform: "none", p: 0, minWidth: 0, color: PASTEL.primary }}
                                    >
                                        {lastOrder?.order_number}
                                    </Button>
                                }
                            />
                            <Row label="Nama Pengirim" value={lastOrder?.cartLast?.sender_name || "-"} />
                            <Row label="Pembayaran" value={lastOrder?.payment_type || "-"} />
                            <Row label="Total" value={`Rp ${new Intl.NumberFormat("id-ID").format(lastOrder?.total || 0)}`} />
                            <Row label="Qty" value={lastOrder?.cartLast?.qty ?? "-"} />
                        </Stack>
                    </Box>
                )}

                <Divider sx={{ mb: 2 }} />

                <Button
                    size="small" variant="outlined" fullWidth onClick={onGenerateLink}
                    startIcon={<LinkIcon fontSize="small" />}
                    sx={{
                        textTransform: "none",
                        color: PASTEL.primary,
                        borderColor: PASTEL.primary,
                        "&:hover": { borderColor: PASTEL.primaryDark, backgroundColor: PASTEL.primarySoft },
                    }}
                >
                    Generate Link Lanjut Chat
                </Button>
            </Box>
        </Box>
    );
}

function Row({ label, value, isLink }) {
    return (
        <Box>
            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
            {isLink ? (
                <Typography
                    variant="body2" component="a" href={value} target="_blank" rel="noreferrer"
                    sx={{ display: "block", wordBreak: "break-all", color: PASTEL.primary }}
                >
                    {value}
                </Typography>
            ) : (
                <Typography variant="body2" fontWeight={500}>{value}</Typography>
            )}
        </Box>
    );
}
